import { useEffect } from 'react'

import { useForm, zodResolver } from '@mantine/form'
import { useDebouncedValue, useFocusWithin } from '@mantine/hooks'
import { z } from 'zod'

import { useGetWalletBalance } from '@repo/common/queries/blockchain/useGetWalletBalance'
import {
  type ExchangeSideType,
  type InSideType,
  type SwapType,
  useGetPoolStats,
  useGetTokenSwapPrice,
} from '@repo/common/queries/fission_dex'
import { useIsSuccess } from '@repo/common/queries/helpers/utilities'
import type { FundSymbol } from '@repo/common/queries/products'

const default_reason = 'Enter an amount'

// eslint-disable-next-line max-lines-per-function, complexity
export const useSwapFields = ({
  fund_symbol,
  in_side,
  starting_tokens_in = 1000,
}: {
  fund_symbol: FundSymbol
  in_side: InSideType
  starting_tokens_in?: number
}) => {
  const form = useForm({
    mode: 'controlled',
    initialValues: {
      in_side,
      exchange_side: 'in',
      tokens_in: starting_tokens_in,
      tokens_out: undefined,
      symbol_in: in_side === 'USDC' ? 'USDC' : fund_symbol,
      symbol_out: in_side !== 'USDC' ? 'USDC' : fund_symbol,
    } as SwapType & {
      in_side: InSideType
      exchange_side: ExchangeSideType
      tokens_in: number
      tokens_out: number | undefined
    },
    validateInputOnChange: true,
    clearInputErrorOnChange: false,
    validate: (values) => zodResolver(get_schema(values))(values),
  })

  const { get_isSuccess, combine_isSuccess } = useIsSuccess()

  const balance_in: number = useGetWalletBalance(
    form.getValues().symbol_in,
  ).balance

  const { balance: balance_out, isConnected: wallet_isConnected } =
    useGetWalletBalance(form.getValues().symbol_out)

  const { data: pool_stats } = combine_isSuccess(
    useGetPoolStats({ symbol: fund_symbol }),
  )

  const available_fund_tokens = pool_stats?.fund_tokens ?? 0
  const available_usdc_tokens = pool_stats?.usdc_tokens ?? 0

  const get_schema = (
    values: SwapType & {
      tokens_in: number
      tokens_out: number | undefined
    },
  ) => {
    return z.object({
      tokens_in: z
        .number({ message: default_reason })
        .positive()
        .max(balance_in, {
          message: `Not enough ${values.symbol_in} balance`,
        }),
      tokens_out: z
        .number({ message: default_reason })
        .positive()
        .max(
          values.symbol_out == 'USDC'
            ? available_usdc_tokens
            : available_fund_tokens,
          {
            message: `Not enough ${values.symbol_out} available`,
          },
        ),
    })
  }

  const tokens_in_focus = useFocusWithin()
  const tokens_out_focus = useFocusWithin()

  const [debounced_form_values] = useDebouncedValue(form.getValues(), 500, {
    leading: true,
  })
  const { data: swap_data } = combine_isSuccess(
    useGetTokenSwapPrice({ ...debounced_form_values, refetchInterval: 30_000 }),
  )

  // needs to default to true to prevent disabling input when fetching new swap data
  const has_liquidity = swap_data?.has_liquidity ?? true

  form.watch('symbol_in', ({ previousValue, value }) => {
    const { tokens_in, tokens_out } = form.getValues()

    form.setValues({
      in_side: value === 'USDC' ? 'USDC' : 'TOKEN',
      tokens_in: tokens_out,
      tokens_out: tokens_in,
      symbol_out: previousValue,
    })
  })

  form.watch('symbol_out', ({ previousValue }) => {
    form.setFieldValue('symbol_in', previousValue)
  })

  // eslint-disable-next-line complexity
  useEffect(() => {
    if (swap_data?.usdc_price == null || swap_data?.usdc_price <= 0) return

    if (form.getValues().exchange_side === 'in') {
      const value = form.getValues().tokens_in
      if (value == null) return

      form.setValues({
        tokens_out:
          form.getValues().symbol_in == 'USDC'
            ? value / swap_data.usdc_price
            : value * swap_data.usdc_price,
      })
    } else {
      const value = form.getValues().tokens_out
      if (value == null) return

      form.setValues({
        tokens_in:
          form.getValues().symbol_in == 'USDC'
            ? value * swap_data.usdc_price
            : value / swap_data.usdc_price,
      })
    }
    // form changes every time
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swap_data?.usdc_price])

  form.watch('tokens_in', ({ value }) => {
    if (!tokens_in_focus.focused) return
    if (swap_data?.usdc_price == null || swap_data?.usdc_price <= 0) {
      form.setValues({ exchange_side: 'in' })
      return
    }
    if (value == null) return

    if (form.getValues().symbol_in == 'USDC') {
      form.setValues({
        tokens_out: value / swap_data.usdc_price,
        exchange_side: 'in',
      })
    } else {
      form.setValues({
        tokens_out: value * swap_data.usdc_price,
        exchange_side: 'in',
      })
    }
  })

  form.watch('tokens_out', ({ value }) => {
    if (!tokens_out_focus.focused) return
    if (swap_data?.usdc_price == null || swap_data?.usdc_price <= 0) {
      form.setValues({ exchange_side: 'out' })
      return
    }
    if (value == null) return

    if (form.getValues().symbol_in == 'USDC') {
      form.setValues({
        tokens_in: value * swap_data.usdc_price,
        exchange_side: 'out',
      })
    } else {
      form.setValues({
        tokens_in: value / swap_data.usdc_price,
        exchange_side: 'out',
      })
    }
  })

  const is_loading = get_isSuccess() == false

  const form_valid = form.isValid() && !is_loading

  const error_reason = has_liquidity
    ? get_schema(form.getValues()).safeParse(form.getValues()).error?.errors[0]
        ?.message || default_reason
    : 'Insufficient liquidity'

  return {
    form,
    balance_in,
    balance_out,
    wallet_isConnected,
    form_valid,
    paused: false,
    error_reason,
    tokens_in_focus,
    tokens_out_focus,
    available_fund_tokens,
    available_usdc_tokens,
    is_loading,
    swap_data,
  }
}
